import {
  Box,
  styled,
  Tab as MuiTab,
  Tabs as MuiTabs,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';

export type ContentTabSizeSpec = {
  /** 각 탭의 padding (트랙 padding 4 는 아래 `Tabs` 가 따로 갖는다) */
  readonly padding: string;
  readonly fontSize: number;
  readonly lineHeight: number;
};

export type ContentTabSizeName = 'small' | 'regular' | 'medium';

/**
 * 사이즈 프리셋.
 * `small` 은 WorkSNB 현행 값, `medium` 은 캘린더 시안(`Segment & Tab`)이자
 * BoardForm 이 sx 로 덮어쓰던 값, `regular` 는 그 사이.
 */
export const CONTENT_TAB_SIZE_SPEC: Record<
  ContentTabSizeName,
  ContentTabSizeSpec
> = {
  small: { padding: '4px 8px', fontSize: 12, lineHeight: 18 },
  regular: { padding: '8px', fontSize: 14, lineHeight: 20 },
  medium: { padding: '8px', fontSize: 16, lineHeight: 24 },
};

/** 프리셋 이름, 또는 맞는 프리셋이 없을 때 값을 직접 */
export type ContentTabSize = ContentTabSizeName | ContentTabSizeSpec;

export const resolveContentTabSize = (
  size: ContentTabSize,
): ContentTabSizeSpec =>
  typeof size === 'string' ? CONTENT_TAB_SIZE_SPEC[size] : size;

export const Tabs = styled(MuiTabs, {
  shouldForwardProp: (prop) => prop !== 'isFullWidth',
})<{ isFullWidth?: boolean }>`
  display: flex;
  padding: 4px;
  align-items: center;
  /* 탭 사이 간격은 여기서 못 준다 — MUI 루트의 자식은 스크롤러 하나뿐이고
     실제 탭들은 .MuiTabs-list 안에 있다. 지금은 탭이 서로 붙어 있다(gap 0).
     시안(Segment & Tab)은 4 지만, 기존 화면 4곳을 안 건드리기로 해서 0 을 유지한다. */
  border-radius: 8px;
  background: #f3f5f6;
  min-height: auto;
  position: relative;
  ${({ isFullWidth }) => (isFullWidth ? 'width: 100%;' : '')}

  .MuiTabs-indicator {
    display: none;
  }
`;

export const Tab = styled(MuiTab, {
  shouldForwardProp: (prop) => prop !== 'isFullWidth' && prop !== 'sizeSpec',
})<{ isFullWidth?: boolean; sizeSpec: ContentTabSizeSpec }>`
  z-index: 2;
  display: flex;
  gap: 4px;

  min-width: auto;
  min-height: auto;

  padding: ${({ sizeSpec }) => sizeSpec.padding};

  /* 글자 크기는 라벨 안쪽 Typography 가 받는다 — TabTitle/TabCount 에 prop 을 내리지 않기 위함 */
  & .MuiTypography-root {
    font-size: ${({ sizeSpec }) => sizeSpec.fontSize}px;
    line-height: ${({ sizeSpec }) => sizeSpec.lineHeight}px;
  }

  ${({ isFullWidth }) =>
    isFullWidth
      ? `
    flex: 1 1 0;
    min-width: 0;
    max-width: none;
  `
      : ''}
`;

export const TabContent = styled(Box)`
  display: flex;

  justify-content: center;
  align-items: center;
  gap: 4px;

  border-radius: 4px;
`;

export const TabWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isFullWidth',
})<{ isFullWidth?: boolean }>`
  position: relative;
  display: flex;
  ${({ isFullWidth }) => (isFullWidth ? 'width: 100%;' : '')}
`;

export const SelectedTabIndicator = styled(motion.div)`
  position: absolute;
  top: 4px;
  bottom: 4px;
  background: #fff;
  border-radius: 4px;
  /* 시안(v2.01 / General / Button / Segment & Tab) 값 — blur 2 */
  box-shadow: 0 0 2px 0 rgba(0, 0, 0, 0.08);
  z-index: 1;
`;

export const TabTitle = styled(Typography)`
  display: inline;
  /* MUI Tab 이 theme.typography.button(=500) 을 깔아둔다.
     시안은 Pretendard Regular 라 여기서 400 으로 되돌린다 */
  font-weight: 400;
`;

/** 굵기는 선택 여부에 따라 호출부(index.tsx)가 sx 로 준다 — 700 / 400 */
export const TabCount = styled(TabTitle)`
  color: #06c;
`;
